import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  type ColumnDef,
  getCoreRowModel,
  type TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Form } from '@/components/ui/form';
import { displayToastSuccess } from '@/helpers/display-toast';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { DeleteSeniorityModal } from '@/modules/settings/components/seniorities/delete-seniority-modal';
import { SenioritiesTable } from '@/modules/settings/components/seniorities/seniorities-table';
import { getSenioritiesColumns } from '@/modules/settings/constants/seniorities-table-columns';
import { useSeniorities } from '@/modules/settings/hooks/seniorities/use-seniorities';
import { useUpdateSeniorities } from '@/modules/settings/hooks/seniorities/use-update-seniorities';
import { SeniorityFormData } from '@/modules/settings/types/seniority';
import {
  SenioritiesFormData,
  senioritiesFormSchema,
} from '@/modules/settings/validations/seniority-validation';
import { compareFormValues } from '@/utils/form-state.utils';

import { SettingsPageWrapper } from '../../components/settings-page-wrapper';
import { SettingsOutletContext } from '../../types/settings-footer';

let nextTempId = -1;

export const SenioritiesPage = () => {
  const { t } = useTranslation('authenticated.settings');
  const navigate = useNavigate();
  const { setFooterState } = useOutletContext<SettingsOutletContext>();

  const { data: seniorities, isLoading, error, refetch } = useSeniorities();
  const updateSenioritiesMutation = useUpdateSeniorities();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [seniorityToDelete, setSeniorityToDelete] = useState<SeniorityFormData | null>(null);

  // Track initial data to compare against
  const initialDataRef = useRef<SeniorityFormData[]>([]);

  // Helper function to map seniorities from API to form data
  const mapSenioritiesToFormData = useCallback((sens: typeof seniorities): SeniorityFormData[] => {
    return sens
      ? sens.map(sen => ({
          seniorityId: sen.id,
          name: sen.name,
          memberCount: sen.memberCount,
        }))
      : [];
  }, []);

  // Initialize form default values
  const defaultValues = useMemo(() => {
    return { seniorities: mapSenioritiesToFormData(seniorities) };
  }, [seniorities, mapSenioritiesToFormData]);

  const form = useForm<SenioritiesFormData>({
    resolver: zodResolver(senioritiesFormSchema),
    mode: 'onChange',
    defaultValues,
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'seniorities',
  });

  // Hook to guard against navigation when there are unsaved changes
  const unsavedChangesGuard = useUnsavedChangesGuard({
    isDirty: form.formState.isDirty,
    onDiscard: () => form.reset(),
  });

  // Store initial data when seniorities load
  useEffect(() => {
    if (seniorities) {
      const formData = mapSenioritiesToFormData(seniorities);
      initialDataRef.current = formData;
      form.reset({ seniorities: formData });
    }
  }, [seniorities, form, mapSenioritiesToFormData]);

  const handleSave = useCallback(async () => {
    const isValid = await form.trigger();
    if (!isValid) return;

    const formData = form.getValues();

    // Prepare seniorities for update (filter out empty names)
    const senioritiesToUpdate = formData.seniorities
      .filter(sen => sen.name.trim().length > 0)
      .map(sen => ({
        id: sen.seniorityId > 0 ? sen.seniorityId : undefined,
        name: sen.name.trim(),
      }));

    try {
      await updateSenioritiesMutation.mutateAsync(senioritiesToUpdate);
      // Refetch to get updated data with new IDs
      await refetch();
      displayToastSuccess(t('pages.seniorities.saveSuccess'));
    } catch {
      // Error handled by mutation
    }
  }, [form, updateSenioritiesMutation, refetch, t]);

  // Compare current state with initial using utility
  const hasActualChanges = useCallback(() => {
    const currentValues = form.getValues().seniorities;
    const initialValues = initialDataRef.current;
    return compareFormValues(currentValues, initialValues);
  }, [form]);

  // Update footer state - simple direct effect
  useEffect(() => {
    const hasChanges = hasActualChanges();
    const isValid = form.formState.isValid;

    setFooterState({
      isVisible: true,
      isEnabled: hasChanges && isValid,
      isLoading: updateSenioritiesMutation.isPending,
      onSave: handleSave,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.formState.isValid,
    form.formState.isDirty, // Trigger when form changes
    updateSenioritiesMutation.isPending,
    fields.length, // Trigger when seniorities added/removed
  ]);

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      setFooterState({
        isVisible: false,
        isEnabled: false,
        isLoading: false,
        onSave: () => {},
      });
    };
  }, [setFooterState]);

  const handleAddSeniority = () => {
    const newSeniority: SeniorityFormData = {
      seniorityId: nextTempId--,
      name: '',
      memberCount: 0,
    };
    append(newSeniority);
  };

  const handleDeleteClick = useCallback(
    (seniority: SeniorityFormData, index: number) => {
      // For new seniorities (client-side only with negative IDs), just remove from form
      if (seniority.seniorityId < 0) {
        remove(index);
        return;
      }

      // For existing seniorities, check if there are unsaved changes
      const shouldProceed = unsavedChangesGuard.checkUnsavedChanges(() => {
        // This callback executes after user discards changes
        setSeniorityToDelete(seniority);
        setDeleteModalOpen(true);
      });

      if (shouldProceed) {
        // Form is clean, proceed immediately with delete
        setSeniorityToDelete(seniority);
        setDeleteModalOpen(true);
      }
    },
    [remove, unsavedChangesGuard]
  );

  const handleDeleteSuccess = async () => {
    setDeleteModalOpen(false);
    setSeniorityToDelete(null);
    // Refetch data and reset form to clean state
    await refetch();
  };

  const columns = useMemo<ColumnDef<SeniorityFormData>[]>(
    () =>
      getSenioritiesColumns({
        onDelete: handleDeleteClick,
        control: form.control,
        t,
        navigate,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const table = useReactTable({
    data: fields,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  } as TableOptions<SeniorityFormData>);

  const unsavedChangesGuardProps = useMemo(
    () => ({
      isModalOpen: unsavedChangesGuard.isModalOpen,
      onDiscard: unsavedChangesGuard.handleModalDiscard,
      onCancel: unsavedChangesGuard.handleModalCancel,
    }),
    [
      unsavedChangesGuard.isModalOpen,
      unsavedChangesGuard.handleModalDiscard,
      unsavedChangesGuard.handleModalCancel,
    ]
  );

  return (
    <SettingsPageWrapper
      isLoading={isLoading}
      error={error}
      onRetry={() => {
        void refetch();
      }}
      unsavedChangesGuard={unsavedChangesGuardProps}
    >
      <Card className="p-6">
        {/* Card Header */}
        <div className="mb-4 flex items-center justify-between gap-4">
          <h2 className="text-lg font-semibold leading-7">{t('pages.seniorities.title')}</h2>
          <Button variant="ghost" className="hidden h-9 px-3 py-2 text-sm font-medium">
            {t('pages.seniorities.configureRates')}
          </Button>
        </div>

        {/* Description */}
        <p className="mb-4 text-xs text-muted-foreground">{t('pages.seniorities.description')}</p>

        {/* Seniorities Count */}
        <p className="mb-4 text-xs text-muted-foreground">
          {t('pages.seniorities.allSeniorities', { count: seniorities?.length || 0 })}
        </p>

        {/* Table */}
        <Form {...form}>
          <form>
            <SenioritiesTable
              table={table}
              onAddSeniority={handleAddSeniority}
              hasSeniorities={fields.length > 0}
              isLoading={isLoading}
            />
          </form>
        </Form>
      </Card>

      {/* Delete Seniority Modal */}
      <DeleteSeniorityModal
        seniority={seniorityToDelete}
        allSeniorities={seniorities || []}
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSeniorityToDelete(null);
        }}
        onSuccess={handleDeleteSuccess}
      />
    </SettingsPageWrapper>
  );
};
